/**
 * Settings → Email: how often MyDeck checks the inbox.
 *
 * One tap for the common choices; "Custom" for anything from 5 minutes to 24
 * hours. A preset saves the moment it is tapped — there is nothing to confirm
 * and nothing to undo that another tap doesn't undo. A custom value saves on
 * Save or Enter, after it reads back in words ("every 1 h 30 min"), so a typo
 * is visible before it takes effect.
 *
 * The line underneath says what the choice *means*: when the next check is
 * and how many that is a day. The schedule changes at once on the server; no
 * restart (services/sync_schedule.py).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Clock } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button, Input, Segmented } from "@/components/ui/primitives";
import { SYNC_INTERVAL_BOUNDS, syncApi, type SyncStatus } from "@/lib/api/client";
import {
  checksPerDay,
  describeInterval,
  parseCustom,
  splitForEditing,
  SYNC_PRESETS,
  type IntervalUnit,
} from "@/lib/syncInterval";

type Choice = `${number}` | "custom";

const timeFormat = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: "Asia/Kolkata",
});

export function SyncFrequency({ status }: { status: SyncStatus }) {
  const qc = useQueryClient();
  const reduce = useReducedMotion();
  const current = status.interval_minutes;
  const isPreset = SYNC_PRESETS.some((p) => p.minutes === current);

  // The segment shown as selected. Opening "Custom" is a local state until a
  // value is saved; a preset tap is saved immediately.
  const [customOpen, setCustomOpen] = React.useState(!isPreset);
  const selected: Choice = customOpen ? "custom" : (`${current}` as Choice);

  const initial = splitForEditing(current);
  const [draft, setDraft] = React.useState(initial.value);
  const [unit, setUnit] = React.useState<IntervalUnit>(initial.unit);
  const parsed = parseCustom(draft, unit);

  const save = useMutation({
    mutationFn: syncApi.setInterval,
    onSuccess: (next) => {
      qc.setQueryData(["sync", "status"], next);
      toast.success(`MyDeck will check ${describeInterval(next.interval_minutes)}.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const choose = (value: Choice) => {
    if (value === "custom") {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    const minutes = Number(value);
    if (minutes !== current) save.mutate(minutes);
  };

  const submitCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if ("minutes" in parsed && parsed.minutes !== current) save.mutate(parsed.minutes);
  };

  return (
    <div className="mt-5 rounded-xl border border-hairline bg-base/40 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 id="sync-frequency-label" className="text-sm font-medium text-ink">
          Check for new mail
        </h3>
        <span className="text-xs text-muted">
          {capitalise(describeInterval(current))}
          {!status.interval_saved && " · the server's default until you pick"}
        </span>
      </div>

      {/* A 3 × 2 grid on a phone rather than a ragged wrap or a sideways scroll. */}
      <Segmented
        label="How often to check for new mail"
        value={selected}
        onChange={choose}
        className="mt-3 grid w-full grid-cols-3 sm:inline-flex sm:w-auto"
        options={[
          ...SYNC_PRESETS.map((p) => ({ value: `${p.minutes}` as Choice, label: p.label })),
          {
            value: "custom" as Choice,
            label: !isPreset && !customOpen ? describeInterval(current) : "Custom…",
          },
        ]}
      />

      <AnimatePresence initial={false}>
        {customOpen && (
          <motion.form
            key="custom"
            onSubmit={submitCustom}
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-2 pt-3">
              <span className="text-sm text-muted">Every</span>
              <Input
                aria-label="How many"
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-20 text-right tabular-nums"
                aria-invalid={"error" in parsed}
                aria-describedby="sync-custom-hint"
              />
              <Segmented
                label="Unit"
                value={unit}
                onChange={setUnit}
                options={[
                  { value: "minutes", label: "minutes" },
                  { value: "hours", label: "hours" },
                ]}
              />
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={"error" in parsed || save.isPending || parsed.minutes === current}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
            <p
              id="sync-custom-hint"
              className={"error" in parsed ? "mt-2 text-xs text-warn" : "mt-2 text-xs text-faint"}
            >
              {"error" in parsed
                ? parsed.error
                : `${describeInterval(parsed.minutes)} — anywhere from ${SYNC_INTERVAL_BOUNDS.min} minutes to ${SYNC_INTERVAL_BOUNDS.max / 60} hours.`}
            </p>
          </motion.form>
        )}
      </AnimatePresence>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted">
        <Clock className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
        {status.next_run_at
          ? `Next check around ${timeFormat.format(new Date(status.next_run_at))} · ${checksPerDay(current)}.`
          : `${capitalise(checksPerDay(current))}. Sync now checks straight away.`}
      </p>
    </div>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
